import { useState } from 'react';
import { getApiBase } from '../../lib/api';

/**
 * AI 文字小幫手：提示 + 選填系統指令，Llama 生成文字
 */
export function AiTextTool() {
  const [prompt, setPrompt] = useState('');
  const [system, setSystem] = useState('');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiBase = getApiBase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || !apiBase) {
      if (!apiBase) setError('請設定 VITE_API_BASE 後端網址');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/ai/text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: prompt.trim(),
          system: system.trim() || undefined,
        }),
      });
      const data = (await res.json()) as {
        success?: boolean;
        text?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(data.message ?? `錯誤 ${res.status}`);
        setText('');
        return;
      }
      setText(data.text ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : '請求失敗');
      setText('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              系統指令（選填，例如：你是一位專業編輯）
            </label>
            <input
              type="text"
              value={system}
              onChange={e => setSystem(e.target.value)}
              placeholder="選填"
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              disabled={!apiBase}
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              你的提示
            </label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="輸入想讓 AI 生成的內容描述..."
              rows={4}
              className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              disabled={!apiBase}
            />
          </div>
          {!apiBase && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              請在環境變數設定 VITE_API_BASE 指向後端 API 網址。
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !apiBase}
            className="rounded bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? '生成中…' : '生成文字'}
          </button>
        </form>
      </div>

      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
          生成結果
        </h2>
        {text ? (
          <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">
            {text}
          </p>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">
            輸入提示並送出後，將顯示 AI 生成內容。
          </p>
        )}
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          由 Cloudflare Workers
          AI（Llama）根據你的提示生成文字，可選填「系統指令」來設定 AI
          角色（例如：摘要、翻譯、故事創作）。資料在邊緣節點處理，回應快速。
        </p>
      </section>
    </>
  );
}
